import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  HiOutlineCpuChip,
  HiOutlineMagnifyingGlass,
  HiOutlineCheckCircle,
} from "react-icons/hi2";

const API_URL = "https://desarrollotecnologicoar.com/api5";
const CATALOGO_EQUIPOS_URL = "https://desarrollotecnologicoar.com/api19/cotizaciones/catalogo-equipos/todos";

const NIVELES = [
  { value: 0, label: "Sin competencia" },
  { value: 1, label: "Básico" },
  { value: 2, label: "Intermedio" },
  { value: 3, label: "Competente" },
  { value: 4, label: "Avanzado" },
  { value: 5, label: "Experto" },
];

const nivelLabel = (v) => NIVELES.find((n) => n.value === v)?.label || "Sin competencia";

const nivelColor = (v) => {
  if (v >= 5) return "text-emerald-600";
  if (v >= 4) return "text-teal-600";
  if (v >= 3) return "text-blue-600";
  if (v >= 2) return "text-amber-600";
  if (v >= 1) return "text-orange-500";
  return "text-slate-400";
};

const CompetenciasTecnicas = () => {
  const [searchParams] = useSearchParams();
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoId, setTecnicoId] = useState(searchParams.get("tecnico_id") || "");
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [catalogo, setCatalogo] = useState({});
  const [niveles, setNiveles] = useState({}); // { modelo_equipo: nivel }
  const [busqueda, setBusqueda] = useState("");
  const [marca, setMarca] = useState("Todas");
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => { fetchTecnicos(); fetchCatalogo(); }, []);
  useEffect(() => { if (tecnicoId) fetchCompetencias(tecnicoId); }, [tecnicoId]);

  const fetchTecnicos = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/tecnicos`);
      setTecnicos(data);
    } catch (err) {
      console.error("Error al obtener tecnicos:", err);
    }
  };

  const fetchCatalogo = async () => {
    try {
      const { data } = await axios.get(CATALOGO_EQUIPOS_URL);
      const marcas = {};
      // El endpoint "/todos" incluye modelos descontinuados (activo:false); se muestran
      // igual porque pueden seguir instalados en campo y requerir soporte.
      (data?.items || [])
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        .forEach((item) => {
          if (!marcas[item.marca]) marcas[item.marca] = [];
          marcas[item.marca].push({ nombre: item.nombre, activo: item.activo });
        });
      setCatalogo(marcas);
    } catch (err) {
      console.error("Error al obtener catalogo de equipos:", err);
      setMensaje({ tipo: "error", texto: "No se pudo cargar el catalogo de equipos." });
    }
  };

  const fetchCompetencias = async (id) => {
    setLoading(true);
    setMensaje(null);
    try {
      const { data } = await axios.get(`${API_URL}/competencias/tecnico/${id}`);
      const mapa = {};
      data.forEach((c) => { mapa[c.modelo_equipo] = c.nivel_competencia; });
      setNiveles(mapa);
    } catch (err) {
      console.error("Error al obtener competencias:", err);
      setMensaje({ tipo: "error", texto: "No se pudieron cargar las competencias del tecnico." });
    } finally {
      setLoading(false);
    }
  };

  const tecnicosVisibles = useMemo(
    () => tecnicos.filter((t) => mostrarInactivos || t.estatus === "Activo"),
    [tecnicos, mostrarInactivos]
  );

  const tecnicoSeleccionado = tecnicos.find((t) => String(t.id) === String(tecnicoId));

  const marcas = Object.keys(catalogo);

  const marcasFiltradas = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    const resultado = {};
    Object.entries(catalogo).forEach(([nombreMarca, modelos]) => {
      if (marca !== "Todas" && nombreMarca !== marca) return;
      const modelosFiltrados = (modelos || []).filter((m) =>
        !texto || m.nombre.toLowerCase().includes(texto)
      );
      if (modelosFiltrados.length > 0) resultado[nombreMarca] = modelosFiltrados;
    });
    return resultado;
  }, [catalogo, busqueda, marca]);

  const setNivel = (modelo, valor) => {
    setNiveles((prev) => ({ ...prev, [modelo]: Number(valor) }));
  };

  const guardarCompetencias = async () => {
    if (!tecnicoId) return;
    setGuardando(true);
    setMensaje(null);
    try {
      const competencias = Object.entries(niveles)
        .filter(([, nivel]) => Number(nivel) >= 1)
        .map(([modelo_equipo, nivel_competencia]) => ({ modelo_equipo, nivel_competencia: Number(nivel_competencia) }));

      await axios.put(`${API_URL}/competencias/tecnico/${tecnicoId}`, { competencias });
      setMensaje({ tipo: "ok", texto: "Competencias guardadas correctamente." });
    } catch (err) {
      console.error("Error al guardar competencias:", err);
      setMensaje({ tipo: "error", texto: err.response?.data?.error || "Error al guardar las competencias." });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Competencias Técnicas</h1>

      <div className="card p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label">Técnico</label>
            <select className="input" value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
              <option value="">-- Selecciona --</option>
              {tecnicosVisibles.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre_tecnico}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-500">
              <input type="checkbox" checked={mostrarInactivos} onChange={(e) => setMostrarInactivos(e.target.checked)} />
              Incluir técnicos inactivos
            </label>
          </div>

          <div>
            <label className="label">Sucursal</label>
            <div className="input bg-surface-app text-texttone-secondary">{tecnicoSeleccionado?.sucursal || "-"}</div>
          </div>

          <div>
            <label className="label">Buscar máquina</label>
            <div className="relative">
              <HiOutlineMagnifyingGlass className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input className="input pl-9" placeholder="Ej. TechStyleCNC" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">Marca</label>
            <select className="input" value={marca} onChange={(e) => setMarca(e.target.value)}>
              <option value="Todas">Todas</option>
              {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {mensaje && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${mensaje.tipo === "ok" ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20" : "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20"}`}>
          {mensaje.texto}
        </div>
      )}

      {!tecnicoId ? (
        <div className="card p-8 text-center text-slate-400">Selecciona un técnico para administrar sus competencias.</div>
      ) : loading ? (
        <div className="card p-8 text-center text-slate-400">Cargando competencias...</div>
      ) : Object.keys(marcasFiltradas).length === 0 ? (
        <div className="card p-8 text-center text-slate-400">No se encontraron máquinas para el filtro actual.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(marcasFiltradas).map(([nombreMarca, modelos]) => (
            <div key={nombreMarca} className="card overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                <HiOutlineCpuChip className="w-5 h-5 text-slate-400" />
                <h2 className="section-title uppercase">{nombreMarca}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="table-header">Modelo</th>
                      <th className="table-header">Nivel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelos.map((modelo) => {
                      const nivel = niveles[modelo.nombre] ?? 0;
                      return (
                        <tr key={modelo.nombre} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="table-cell font-medium text-slate-800">
                            {modelo.nombre}
                            {!modelo.activo && (
                              <span className="badge-gray ml-2 align-middle">Descontinuado</span>
                            )}
                          </td>
                          <td className="table-cell">
                            <select
                              className={`input max-w-[220px] font-semibold ${nivelColor(nivel)}`}
                              value={nivel}
                              onChange={(e) => setNivel(modelo.nombre, e.target.value)}
                            >
                              {NIVELES.map((n) => (
                                <option key={n.value} value={n.value}>{n.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="flex justify-end sticky bottom-4">
            <button onClick={guardarCompetencias} disabled={guardando} className="btn-primary shadow-lg">
              <HiOutlineCheckCircle className="w-4 h-4" />
              {guardando ? "Guardando..." : "Guardar competencias"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetenciasTecnicas;
